import React, { useMemo, useState } from 'react';
import { IdCardIcon, UserRoundIcon, MapPinIcon, MailIcon, PhoneIcon, CalendarIcon, CreditCardIcon, ShieldCheckIcon, KeyRoundIcon, ChevronDownIcon, CheckIcon } from 'lucide-react';
import { StatusBar } from './StatusBar';
import { ScreenHeader } from './ScreenHeader';
import { SuccessModal } from './SuccessModal';
const SECURITY_QUESTIONS = ['What was the name of your first school?', "What is your mother's maiden name?", 'What was the name of your first pet?', 'In which city were you born?', "What is your favourite teacher's name?"];
const YEARS = Array.from({
  length: 80
}, (_, i) => 2008 - i);
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS = Array.from({
  length: 31
}, (_, i) => i + 1);
const SECTIONS = ['Personal', 'Contact', 'Account', 'Security'] as const;
export function CreateAccountScreen({
  onBack,
  onComplete
}: {
  onBack: () => void;
  onComplete: () => void;
}) {
  const [form, setForm] = useState({
    nic: '',
    firstName: '',
    lastName: '',
    birthYear: '',
    birthMonth: '',
    birthDay: '',
    address: '',
    email: '',
    mobile: '',
    accountNo: '',
    q1: SECURITY_QUESTIONS[0],
    a1: '',
    q2: SECURITY_QUESTIONS[1],
    a2: '',
    q3: SECURITY_QUESTIONS[2],
    a3: ''
  });
  const [agree, setAgree] = useState(false);
  const [success, setSuccess] = useState(false);
  const set = (k: keyof typeof form) => (v: string) => setForm(f => ({
    ...f,
    [k]: v
  }));
  const completion = useMemo(() => {
    const personal = !!(form.nic && form.firstName && form.lastName && form.birthYear);
    const contact = !!(form.address && form.email && form.mobile);
    const account = !!form.accountNo;
    const security = !!(form.a1 && form.a2 && form.a3);
    const done = [personal, contact, account, security].filter(Boolean).length;
    return {
      personal,
      contact,
      account,
      security,
      done,
      total: 4
    };
  }, [form]);
  return <div className="relative flex flex-col w-full h-full overflow-hidden" style={{
    backgroundColor: '#F4F7FA'
  }} data-id="element-230">
      <div aria-hidden="true" className="pointer-events-none absolute -top-20 -right-20 rounded-full blur-3xl opacity-30" style={{
      width: 280,
      height: 280,
      background: 'radial-gradient(circle, #00B5E0 0%, transparent 70%)'
    }} data-id="element-231" />
      <StatusBar data-id="element-232" />
      <ScreenHeader title="Create Account" onBack={onBack} data-id="element-233" />
      <div className="flex-1 overflow-y-auto pb-3" data-id="element-234">
        <div className="px-5 pb-5" data-id="element-235">
          <div className="relative overflow-hidden rounded-3xl p-5" style={{
          background: 'linear-gradient(135deg, #009AC7 0%, #007FA5 100%)',
          color: '#FFFFFF'
        }} data-id="element-236">
            <div aria-hidden="true" className="absolute -right-10 -bottom-10 rounded-full" style={{
            width: 160,
            height: 160,
            background: 'radial-gradient(circle, rgba(255,255,255,0.18) 0%, transparent 70%)'
          }} data-id="element-237" />
            <div className="flex items-center gap-2 mb-2" data-id="element-238">
              <span style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.06em',
              opacity: 0.95
            }} data-id="element-239">
                WELCOME TO RDB
              </span>
            </div>
            <h2 className="tracking-tight" style={{
            fontSize: 22,
            fontWeight: 700,
            lineHeight: 1.25
          }} data-id="element-240">
              Let's set up your account
            </h2>

            <div className="mt-4 flex items-center gap-2.5" data-id="element-241">
              {SECTIONS.map((label, i) => {
              const states = [completion.personal, completion.contact, completion.account, completion.security];
              const done = states[i];
              return <div key={label} className="flex-1 flex flex-col gap-1.5" data-id="element-242">
                    <div className="h-1 rounded-full transition-colors" style={{
                  backgroundColor: done ? '#4ADE80' : 'rgba(255,255,255,0.3)',
                  boxShadow: done ? '0 0 8px rgba(74, 222, 128, 0.55)' : 'none'
                }} data-id="element-243" />
                    <span style={{
                  fontSize: 10.5,
                  fontWeight: 600,
                  letterSpacing: '0.02em',
                  opacity: done ? 1 : 0.85
                }} data-id="element-244">
                      {label}
                    </span>
                  </div>;
            })}
            </div>
            <div className="mt-3 text-right" style={{
            fontSize: 11,
            fontWeight: 600,
            opacity: 0.85
          }} data-id="element-245">
              {completion.done} of {completion.total} sections complete
            </div>
          </div>
        </div>
        <form className="flex flex-col gap-4 px-5" onSubmit={e => {
        e.preventDefault();
        setSuccess(true);
      }} data-id="element-246">
          <Section index="01" total="04" icon={<UserRoundIcon size={22} style={{
          color: '#009AC7'
        }} data-id="element-248" />} title="Personal Information" description="Enter your details exactly as they appear on your NIC." complete={completion.personal} data-id="element-247">
            <Input label="NIC Number" leadingIcon={<IdCardIcon size={18} data-id="element-250" />} value={form.nic} onChange={set('nic')} placeholder="e.g. 198512345678" data-id="element-249" />
            <div className="grid grid-cols-2 gap-3" data-id="element-251">
              <Input label="First Name" value={form.firstName} onChange={set('firstName')} placeholder="Kasun" data-id="element-252" />
              <Input label="Last Name" value={form.lastName} onChange={set('lastName')} placeholder="Perera" data-id="element-253" />
            </div>
            <div data-id="element-254">
              <FieldLabel icon={<CalendarIcon size={14} data-id="element-256" />} data-id="element-255">
                Date of Birth
              </FieldLabel>
              <div className="grid grid-cols-3 gap-2" data-id="element-257">
                <Select value={form.birthYear} onChange={set('birthYear')} placeholder="Year" options={YEARS.map(String)} data-id="element-258" />
                <Select value={form.birthMonth} onChange={set('birthMonth')} placeholder="Month" options={MONTHS} data-id="element-259" />
                <Select value={form.birthDay} onChange={set('birthDay')} placeholder="Day" options={DAYS.map(String)} data-id="element-260" />
              </div>
            </div>
          </Section>
          <Section index="02" total="04" icon={<MailIcon size={22} style={{
          color: '#009AC7'
        }} data-id="element-262" />} title="Contact Details" description="So we can reach you for important account updates." complete={completion.contact} data-id="element-261">
            <Input label="Residential Address" leadingIcon={<MapPinIcon size={18} data-id="element-264" />} value={form.address} onChange={set('address')} placeholder="House no, Street, City" data-id="element-263" />
            <Input label="Email Address" leadingIcon={<MailIcon size={18} data-id="element-266" />} type="email" value={form.email} onChange={set('email')} placeholder="name@email.com" data-id="element-265" />
            <Input label="Registered Mobile Number" leadingIcon={<PhoneIcon size={18} data-id="element-268" />} inputMode="tel" value={form.mobile} onChange={set('mobile')} placeholder="+94 7X XXX XXXX" hint="Must match the number registered with your RDB account." data-id="element-267" />
          </Section>
          <Section index="03" total="04" icon={<CreditCardIcon size={22} style={{
          color: '#009AC7'
        }} data-id="element-270" />} title="Link Bank Account" description="Connect your existing RDB savings or current account." complete={completion.account} data-id="element-269">
            <Input label="RDB Account Number" leadingIcon={<CreditCardIcon size={18} data-id="element-272" />} inputMode="numeric" value={form.accountNo} onChange={set('accountNo')} placeholder="0123 4567 8910" data-id="element-271" />
          </Section>
          <Section index="04" total="04" icon={<KeyRoundIcon size={22} style={{
          color: '#009AC7'
        }} data-id="element-274" />} title="Security Questions" description="Used to verify you if you ever lose access." complete={completion.security} data-id="element-273">
            <SecurityQuestion index={1} question={form.q1} answer={form.a1} onQuestion={set('q1')} onAnswer={set('a1')} data-id="element-275" />
            <SecurityQuestion index={2} question={form.q2} answer={form.a2} onQuestion={set('q2')} onAnswer={set('a2')} data-id="element-276" />
            <SecurityQuestion index={3} question={form.q3} answer={form.a3} onQuestion={set('q3')} onAnswer={set('a3')} data-id="element-277" />
          </Section>
          <div style={{
          height: 8
        }} data-id="element-278" />
        </form>
      </div>
      <div className="px-5 pt-3 pb-3" style={{
      backgroundColor: 'rgba(255,255,255,0.85)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      borderTop: '1px solid #E5EAF0'
    }} data-id="element-279">
        <label className="flex items-start gap-2.5 mb-2.5 cursor-pointer select-none" data-id="element-280">
          <span className="mt-0.5 shrink-0 flex items-center justify-center rounded-md transition-all" style={{
          width: 22,
          height: 22,
          backgroundColor: agree ? '#009AC7' : '#FFFFFF',
          border: agree ? '1.5px solid #009AC7' : '1.5px solid #C0C0C0'
        }} data-id="element-281">
            {agree && <CheckIcon size={14} color="#FFFFFF" strokeWidth={3} data-id="element-282" />}
          </span>
          <input type="checkbox" checked={agree} onChange={e => setAgree(e.target.checked)} className="sr-only" data-id="element-283" />
          <span style={{
          fontSize: 12.5,
          color: '#1A1A1A',
          lineHeight: 1.55
        }} data-id="element-284">
            I confirm the information is accurate and I agree to RDB's{' '}
            <span style={{
            color: '#009AC7',
            fontWeight: 600
          }} data-id="element-285">
              Terms
            </span>{' '}
            &{' '}
            <span style={{
            color: '#009AC7',
            fontWeight: 600
          }} data-id="element-286">
              Privacy Policy
            </span>
            .
          </span>
        </label>
        <button type="submit" disabled={!agree} onClick={() => agree && setSuccess(true)} className="w-full rounded-2xl text-white font-semibold transition-all active:scale-[0.99] disabled:opacity-40 disabled:cursor-not-allowed" style={{
        backgroundColor: '#009AC7',
        minHeight: 56,
        fontSize: 16,
        boxShadow: agree ? '0 12px 28px -10px rgba(0, 154, 199, 0.6)' : 'none',
        letterSpacing: '0.01em'
      }} data-id="element-287">
          Create Account
        </button>
        <div className="flex items-center justify-center gap-1.5 mt-2.5" data-id="element-288">
          <ShieldCheckIcon size={12} style={{
          color: '#009AC7'
        }} data-id="element-289" />
          <span style={{
          fontSize: 11,
          color: '#525252',
          fontWeight: 500
        }} data-id="element-290">
            Protected by Central Bank of Sri Lanka
          </span>
        </div>
      </div>
      <div className="flex justify-center pb-2" style={{
      backgroundColor: '#FFFFFF'
    }} data-id="element-291">
        <div className="w-[120px] h-[5px] rounded-full bg-neutral-900/80" aria-hidden="true" data-id="element-292" />
      </div>
      <SuccessModal open={success} title="Account Created" message="Welcome to RDB Mobile Banking. Sign in with your NIC and password to get started." ctaLabel="Go to Sign In" onConfirm={() => {
      setSuccess(false);
      onComplete();
    }} data-id="element-293" />
    </div>;
}
function Section({
  index,
  total,
  icon,
  title,
  description,
  complete,
  children
}: {
  index: string;
  total: string;
  icon: React.ReactNode;
  title: string;
  description?: string;
  complete: boolean;
  children: React.ReactNode;
}) {
  return <section className="rounded-3xl bg-white p-5" style={{
    border: '1px solid #E5EAF0',
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 8px 24px -16px rgba(15, 23, 42, 0.12)'
  }} data-id="element-294">
      <div className="flex items-start gap-3 mb-4" data-id="element-295">
        <div className="shrink-0 flex items-center justify-center rounded-2xl" style={{
        width: 44,
        height: 44,
        backgroundColor: '#E6F7FB'
      }} data-id="element-296">
          {icon}
        </div>
        <div className="flex-1 min-w-0" data-id="element-297">
          <div className="flex items-center gap-2" data-id="element-298">
            <h3 className="tracking-tight" style={{
            fontSize: 16,
            fontWeight: 700,
            color: '#1A1A1A',
            lineHeight: 1.3
          }} data-id="element-299">
              {title}
            </h3>
            {complete && <span className="inline-flex items-center justify-center rounded-full" style={{
            width: 18,
            height: 18,
            backgroundColor: '#009AC7'
          }} aria-label="Section complete" data-id="element-300">
                <CheckIcon size={11} color="#FFFFFF" strokeWidth={3.5} data-id="element-301" />
              </span>}
          </div>
          {description && <div className="mt-0.5" style={{
          fontSize: 12,
          color: '#64748B',
          lineHeight: 1.55
        }} data-id="element-302">
              {description}
            </div>}
        </div>
        <span className="shrink-0 px-2 py-0.5 rounded-full" style={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.02em',
        color: '#009AC7',
        backgroundColor: '#E6F7FB'
      }} data-id="element-303">
          {index}/{total}
        </span>
      </div>
      <div className="flex flex-col gap-3.5" data-id="element-304">{children}</div>
    </section>;
}
function FieldLabel({
  children,
  icon
}: {
  children: React.ReactNode;
  icon?: React.ReactNode;
}) {
  return <div className="mb-1.5 flex items-center gap-1.5" style={{
    fontSize: 12,
    fontWeight: 600,
    color: '#1A1A1A',
    letterSpacing: '0.02em'
  }} data-id="element-305">
      {icon && <span style={{
      color: '#64748B'
    }} data-id="element-306">
          {icon}
        </span>}
      {children}
    </div>;
}
function Input({
  label,
  leadingIcon,
  value,
  onChange,
  placeholder,
  type = 'text',
  inputMode,
  hint
}: {
  label: string;
  leadingIcon?: React.ReactNode;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  inputMode?: 'text' | 'tel' | 'numeric' | 'email';
  hint?: string;
}) {
  const [focused, setFocused] = useState(false);
  const filled = value.length > 0;
  return <div data-id="element-307">
      <FieldLabel data-id="element-308">{label}</FieldLabel>
      <div className="flex items-center gap-2.5 px-3.5 rounded-2xl transition-all" style={{
      minHeight: 54,
      backgroundColor: focused ? '#FFFFFF' : '#F8FAFC',
      border: focused ? '1.5px solid #009AC7' : filled ? '1.5px solid #CBD5E1' : '1.5px solid #E5EAF0',
      boxShadow: focused ? '0 0 0 4px rgba(0,154,199,0.12)' : 'none'
    }} data-id="element-309">
        {leadingIcon && <div className="shrink-0 flex items-center justify-center" style={{
        color: focused ? '#009AC7' : '#64748B'
      }} data-id="element-310">
            {leadingIcon}
          </div>}
        <input type={type} inputMode={inputMode} value={value} onChange={e => onChange(e.target.value)} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} placeholder={placeholder} className="flex-1 min-w-0 bg-transparent outline-none" style={{
        fontSize: 15,
        color: '#1A1A1A',
        minHeight: 50,
        fontWeight: 500
      }} data-id="element-311" />
        {filled && <CheckIcon size={16} style={{
        color: '#10B981'
      }} className="shrink-0" aria-hidden="true" data-id="element-312" />}
      </div>
      {hint && <div className="mt-1.5 ml-1" style={{
      fontSize: 11.5,
      color: '#64748B',
      lineHeight: 1.5
    }} data-id="element-313">
          {hint}
        </div>}
    </div>;
}
function Select({
  value,
  onChange,
  placeholder,
  options
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  options: string[];
}) {
  const filled = value.length > 0;
  return <div className="relative flex items-center px-3 rounded-2xl transition-all" style={{
    minHeight: 54,
    backgroundColor: '#F8FAFC',
    border: filled ? '1.5px solid #CBD5E1' : '1.5px solid #E5EAF0'
  }} data-id="element-314">
      <select value={value} onChange={e => onChange(e.target.value)} className="flex-1 min-w-0 bg-transparent outline-none appearance-none pr-5" style={{
      fontSize: 14,
      color: filled ? '#1A1A1A' : '#94A3B8',
      minHeight: 50,
      fontWeight: 500
    }} data-id="element-315">
        <option value="" data-id="element-316">{placeholder}</option>
        {options.map(o => <option key={o} value={o} data-id="element-317">
            {o}
          </option>)}
      </select>
      <ChevronDownIcon size={16} style={{
      color: '#64748B'
    }} className="absolute right-3 pointer-events-none" data-id="element-318" />
    </div>;
}
function SecurityQuestion({
  index,
  question,
  answer,
  onQuestion,
  onAnswer
}: {
  index: number;
  question: string;
  answer: string;
  onQuestion: (v: string) => void;
  onAnswer: (v: string) => void;
}) {
  return <div className="rounded-2xl p-3" style={{
    backgroundColor: '#F8FAFC',
    border: '1px solid #E5EAF0'
  }} data-id="element-319">
      <div className="flex items-center gap-2 mb-2.5" style={{
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '0.04em',
      color: '#009AC7'
    }} data-id="element-320">
        <span className="inline-flex items-center justify-center rounded-md" style={{
        width: 18,
        height: 18,
        backgroundColor: '#009AC7',
        color: '#FFFFFF',
        fontSize: 10
      }} data-id="element-321">
          {index}
        </span>
        QUESTION {index}
      </div>
      <div className="flex flex-col gap-2.5" data-id="element-322">
        <div data-id="element-323">
          <FieldLabel data-id="element-324">Choose a question</FieldLabel>
          <div className="relative flex items-center px-3.5 rounded-xl bg-white" style={{
          minHeight: 50,
          border: '1.5px solid #E5EAF0'
        }} data-id="element-325">
            <select value={question} onChange={e => onQuestion(e.target.value)} className="flex-1 min-w-0 bg-transparent outline-none appearance-none pr-5" style={{
            fontSize: 14,
            color: '#1A1A1A',
            minHeight: 46,
            fontWeight: 500
          }} data-id="element-326">
              {SECURITY_QUESTIONS.map(q => <option key={q} value={q} data-id="element-327">
                  {q}
                </option>)}
            </select>
            <ChevronDownIcon size={16} style={{
            color: '#64748B'
          }} className="absolute right-3 pointer-events-none" data-id="element-328" />
          </div>
        </div>
        <Input label="Your answer" value={answer} onChange={onAnswer} placeholder="Type your answer" data-id="element-329" />
      </div>
    </div>;
}